import React from "react";
import { CheckCircle2, Lock, PhoneCall, ShieldCheck } from "lucide-react";

const verificationSteps = [
  "Identity verification (Aadhaar, PAN)",
  "Police background check",
  "Previous employment verification",
  "Personal reference checks (minimum 3)",
  "In-person interview and assessment",
  "Training on elderly care protocols",
];

const privacyPoints = [
  "End-to-end encrypted communications",
  "Secure cloud storage (ISO 27001 certified)",
  "No sharing of data with third parties",
  "Regular security audits",
];

const complaintSteps = [
  "24/7 complaint hotline",
  "Response within 2 hours",
  "Investigation and resolution within 48 hours",
  "Care partner replacement if needed",
  "Full refund policy for service failures",
];

const textDark = "#0f2244";
const textSoft = "#3c5677";

const Safety = () => {
  return (
    <div
      className="safety-page"
      style={{
        background:
          "radial-gradient(circle at 12% 6%, #f6fbff 0%, #edf3fb 38%, #e7edf7 70%, #e2e9f4 100%)",
      }}
    >
      <section
        className="safety-hero relative overflow-hidden px-4 py-20 sm:py-24 text-center"
        style={{
         background:
  "linear-gradient(130deg, #3AAFA9 0%, #2f9f98 40%, #268e88 100%)",

        }}
      >
        <div className="safety-hero-orb-a absolute -top-16 -left-20 w-80 h-80 rounded-full" />
        <div className="safety-hero-orb-b absolute -bottom-24 -right-16 w-[22rem] h-[22rem] rounded-full" />
        <div className="safety-hero-grid absolute inset-0" />

        <span className="safety-hero-pill relative inline-block px-7 py-3 rounded-xl text-lg font-semibold bg-white text-[#3AAFA9]">
          Your Trust Matters
        </span>
        <h1 className="safety-hero-title relative mt-10 text-white text-4xl sm:text-6xl font-bold tracking-tight">
          Safety &amp; Compliance
        </h1>
        <p className="safety-hero-sub relative mt-8 text-[#d7e4ff] text-xl sm:text-[2rem] max-w-5xl mx-auto leading-relaxed">
          Every measure we take to keep your parents safe and your data secure
        </p>
      </section>

      <section className="px-4 py-20 sm:py-24 text-center">
        <div className="icon-badge icon-badge-blue w-20 h-20 rounded-full mx-auto flex items-center justify-center text-white">
          <ShieldCheck size={40} />
        </div>
        <h2 className="premium-heading mt-8 text-[1.85rem] sm:text-[2.55rem]" style={{ color: textDark }}>
          Care Partner Verification
        </h2>
        <p className="premium-sub mt-4 text-[1.02rem] sm:text-[1.2rem]" style={{ color: textSoft }}>
          A rigorous 6-step process before anyone meets your parents
        </p>

        <div
          className="safety-card max-w-5xl mx-auto mt-14 rounded-2xl border p-6 sm:p-10"
          style={{ borderColor: "#bfd3f8" }}
        >
          {verificationSteps.map((item, index) => (
            <div
              key={item}
              className="safety-line-item flex items-center gap-5 py-5 sm:py-7 text-left"
              style={{ animationDelay: `${index * 0.08}s` }}
            >
              <span className="step-chip w-11 h-11 rounded-full text-white flex items-center justify-center font-bold text-xl shrink-0">
                {index + 1}
              </span>
              <p className="premium-body text-[1.03rem] sm:text-[1.22rem]" style={{ color: "#223c62" }}>
                {item}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 py-20 sm:py-24 text-center bg-[#e7edf6]">
        <div className="icon-badge icon-badge-orange w-20 h-20 rounded-full mx-auto flex items-center justify-center text-white">
          <Lock size={36} />
        </div>
        <h2 className="premium-heading mt-8 text-[1.85rem] sm:text-[2.55rem]" style={{ color: textDark }}>
          Data Protection &amp; Privacy
        </h2>
        <p className="premium-sub mt-4 text-[1.02rem] sm:text-[1.2rem]" style={{ color: textSoft }}>
          Your family&apos;s information is treated with highest security standards
        </p>

        <div
          className="safety-card safety-card-orange max-w-5xl mx-auto mt-14 rounded-2xl border p-6 sm:p-10 text-left"
          style={{ borderColor: "#f1cda8" }}
        >
          {privacyPoints.map((item, index) => (
            <div
              key={item}
              className="safety-line-item flex items-center gap-4 py-4 sm:py-5"
              style={{ animationDelay: `${index * 0.08}s` }}
            >
              <CheckCircle2 className="shrink-0 text-[#ff7a12]" size={34} />
              <p className="premium-body text-[1.03rem] sm:text-[1.22rem]" style={{ color: "#223c62" }}>
                {item}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 py-20 sm:py-24 text-center">
        <div className="icon-badge icon-badge-blue w-20 h-20 rounded-full mx-auto flex items-center justify-center text-white">
          <PhoneCall size={36} />
        </div>
        <h2 className="premium-heading mt-8 text-[1.85rem] sm:text-[2.55rem]" style={{ color: textDark }}>
          Complaint Resolution Process
        </h2>
        <p className="premium-sub mt-4 text-[1.02rem] sm:text-[1.2rem]" style={{ color: textSoft }}>
          We take every concern seriously with guaranteed response times
        </p>

        <div
          className="safety-card max-w-5xl mx-auto mt-14 rounded-2xl border p-6 sm:p-10"
          style={{ borderColor: "#bfd3f8" }}
        >
          {complaintSteps.map((item, index) => (
            <div
              key={item}
              className="safety-step-box rounded-2xl px-4 sm:px-8 py-6 sm:py-8 mb-5 last:mb-0 flex items-center gap-5 text-left"
              style={{ animationDelay: `${index * 0.08}s` }}
            >
              <span className="step-chip step-chip-square w-12 h-12 rounded-xl text-white flex items-center justify-center font-bold text-2xl shrink-0">
                {index + 1}
              </span>
              <p className="premium-body text-[1.03rem] sm:text-[1.22rem]" style={{ color: "#223c62" }}>
                {item}
              </p>
            </div>
          ))}
        </div>
      </section>

   
    </div>
  );
};

export default Safety;
